import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from 'component-library';
import { Options } from 'component-library/lib/esm/models/ComponentModels';
import { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';

interface SearchableSelectDropdownProps {
  value?: string;
  optionsData: Options[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  renderOption?: (option: Options) => ReactNode;
  getOptionClassName?: (option: Options) => string;
  onValueChosen: (id?: string) => void;
}

export default function SearchableSelectDropdown(props: SearchableSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return props.optionsData;

    return props.optionsData.filter((option) =>
      option.value?.toLowerCase().includes(searchValue.trim().toLowerCase())
    );
  }, [props.optionsData, searchValue]);

  const ref = useDetectClickOutside({
    onTriggered: () => {
      setIsOpen(false);
      setSearchValue('');
    },
  });

  const toggleDropdown = () => {
    if (props.disabled) return;

    setIsOpen((prev) => {
      if (prev) setSearchValue('');
      return !prev;
    });
  };

  return (
    <div ref={ref}>
      <div className='relative'>
        <button
          type='button'
          className='w-full h-10 px-4 py-2 bg-gray-3 prose-paragraphSmaller outline-0 gap-2 whitespace-nowrap overflow-hidden flex justify-between items-center disabled:opacity-70'
          onClick={toggleDropdown}
          disabled={props.disabled}
        >
          <span className={`${!props.value && 'text-gray-40'} truncate`}>
            {props.value ? props.value : props.placeholder}
          </span>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </button>
        {isOpen && (
          <div className='absolute bg-white py-1 mt-2 m-0 w-full z-10 shadow-dropdown border border-gray-10 max-h-[260px] overflow-auto'>
            <div className='px-2 pt-2 pb-1 sticky top-0 bg-white'>
              <Input
                value={searchValue}
                onChange={(value: string) => setSearchValue(value)}
                placeholder={props.searchPlaceholder || 'Search...'}
              />
            </div>
            <ul>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <li
                    className={`py-2 px-4 flex w-full text-gray-100 prose-paragraphSmaller hover:bg-gray-5 cursor-pointer ${
                      props.getOptionClassName?.(opt) || ''
                    }`}
                    key={opt.id || opt.value}
                    onClick={() => {
                      props.onValueChosen(opt.id);
                      setSearchValue('');
                      setIsOpen(false);
                    }}
                  >
                    {props.renderOption ? props.renderOption(opt) : opt.value}
                  </li>
                ))
              ) : (
                <li className='py-2 px-4 flex w-full prose-paragraphSmaller italic text-gray-40 cursor-default'>
                  {props.emptyText || 'Nothing here'}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
